import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import PropTypes from 'prop-types';

import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryNowLoader } from '../../data-table/Loaders';
import {
    businessServiceFilterId,
    siteFilterId,
    statusFilterId,
    testTypeFilterId,
    timeRangeFilterId,
} from '../../filters';
import { Ng1DrillDownButton } from '../../ng1-drill-down/Ng1DrillDownButton';
import useNg1DrillDownService from '../../../services/Ng1DrillDownService';
import { QuickStatusColumnRenderer } from '../../data-table/columns/Status';
import { useDataService } from '../../../services/ApiDataService';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';

const title = t`Bandwidth Test Now Result`;

const dataSource = {
    url: '/ipm/v1/admin/quicktests/',
    dataProperty: 'data',
    totalProperty: 'count',
    isResultsPage: true,
    numericMetrics: ['bandwidth_in', 'bandwidth_out', 'duration'],
    params: {
        searchFields: ['name'],
    },
};

const DEFAULT_COLUMNS = [
    {
        type: COLUMN_TYPES.STATUS,
        title: t`Status`,
        mapping: 'measurement-status',
        width: 105,
        resizable: false,
        renderer: QuickStatusColumnRenderer,
        csvFormatter: (value, row) => row.availability,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`nPoint`,
        mapping: 'name',
        sortable: true,
        width: 200,
        csvFormatter: (value, row) => row.name,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Result`,
        mapping: 'message',
        sortable: true,
        width: 100,
        csvFormatter: (value, row) => row.message,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Bitrate In`,
        mapping: 'bandwidth_in',
        formatter: FORMATTER_TYPES.MEGABITRATE,
        sortable: true,
        width: 110,
        csvFormatter: (value, row) => row.bandwidth_in,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Bitrate Out`,
        mapping: 'bandwidth_out',
        formatter: FORMATTER_TYPES.MEGABITRATE,
        sortable: true,
        width: 115,
        csvFormatter: (value, row) => row.bandwidth_out,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Volume In`,
        mapping: 'transfer_in',
        formatter: (val) => (val ? `${val}MB` : '--'),
        sortable: true,
        width: 115,
        csvFormatter: (value, row) => row.transfer_in,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Volume out`,
        mapping: 'transfer_out',
        formatter: (val) => (val ? `${val}MB` : '--'),
        sortable: true,
        width: 117,
        csvFormatter: (value, row) => row.transfer_out,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Loss Count In`,
        mapping: 'loss_count_in',
        sortable: true,
        width: 109,
        csvFormatter: (value, row) => row.loss_count_in,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Loss Count Out`,
        mapping: 'loss_count_out',
        sortable: true,
        width: 109,
        csvFormatter: (value, row) => row.loss_count_out,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Loss % In`,
        mapping: 'loss_in',
        formatter: FORMATTER_TYPES.PERCENT,
        sortable: true,
        width: 109,
        csvFormatter: (value, row) => row.loss_in,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Loss % Out`,
        mapping: 'loss_out',
        formatter: FORMATTER_TYPES.PERCENT,
        sortable: true,
        width: 113,
        csvFormatter: (value, row) => row.loss_out,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Port`,
        mapping: 'port',
        sortable: true,
        width: 70,
        csvFormatter: (value, row) => row.port,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Duration`,
        mapping: 'duration',
        sortable: true,
        width: 75,
        csvFormatter: (value, row) => row.duration,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`DSCP`,
        mapping: 'dscp',
        sortable: true,
        width: 80,
        csvFormatter: (value, row) => row.dscp,
    },
];

const WIFI_COLUMNS = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Wi-Fi Network`,
        mapping: 'wifi_ssid',
        sortable: true,
        width: 150,
        resizable: false,
        csvFormatter: (value, row) => row.wifi_ssid,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Wi-Fi BSSID`,
        mapping: 'wifi_bssid',
        width: 150,
        csvFormatter: (value, row) => row.wifi_bssid,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Wi-Fi IP`,
        mapping: 'wifi_ip_address',
        width: 125,
        csvFormatter: (value, row) => row.wifi_ip_address,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Signal Strength (dBm)`,
        mapping: 'wifi_signal_level_dbm',
        width: 80,
        csvFormatter: (value, row) => row.wifi_signal_level_dbm,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Wi-Fi Channel`,
        mapping: 'wifi_channel',
        width: 80,
        csvFormatter: (value, row) => row.wifi_channel,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Wi-Fi Errors`,
        mapping: 'wifi_connection_errors',
        width: 80,
        csvFormatter: (value, row) => row.wifi_connection_errors,
    },
];

const sorters = [{ property: 'name', direction: 'desc' }];

const NG1_DRILLDOWN_COLUMN = {
    type: COLUMN_TYPES.ICON,
    mapping: 'test',
    width: 30,
    uiOnly: true,
    icons: [
        {
            getIcon: (row) => {
                return <Ng1DrillDownButton test={row?.test} />;
            },
            getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
        },
    ],
};

function NetTestNowTestResultPanel(props) {
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);
    dataSource.url = `/ipm/v1/admin/quicktests/${props.id}`;
    const loader = useQueryNowLoader(dataSource);
    const dataService = useDataService();
    const { triggerRef } = props;

    useEffect(() => {
        if (hasNg1DrillDownPrivilege()) {
            setColumns([...DEFAULT_COLUMNS, NG1_DRILLDOWN_COLUMN]);
        }
        (async () => {
            const { adapterType } = await dataService.get(dataSource.url);
            const isWireless = adapterType === 'wireless';

            if (isWireless) {
                setColumns(DEFAULT_COLUMNS.concat(WIFI_COLUMNS));
            }
        })();
    }, [hasNg1DrillDownPrivilege, dataService]);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            triggerRef={triggerRef}
            visibleRows={null}
            deletedMapping="test.deleted"
            showSearch={false}
        />
    );
}

NetTestNowTestResultPanel.propTypes = {
    ...panelPropTypes,
    triggerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

NetTestNowTestResultPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'NetTestResultList',
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, statusFilterId, siteFilterId, testTypeFilterId],
    },
})(NetTestNowTestResultPanel);
